import { useEffect, useMemo, useState } from 'react';
import Hero from '../components/home/Hero';
import ContinueReading from '../components/home/ContinueReading';
import GenreHighlights from '../components/home/GenreHighlights';
import TopAuthors from '../components/home/TopAuthors';
import Testimonials from '../components/home/Testimonials';
import BookSection from '../components/books/BookSection';
import FeaturedBanner from '../components/home/FeaturedBanner';
import AffiliateProgramSection from '../components/home/AffiliateProgramSection';
import { api } from '../lib/api';
import { normalizeBooks } from '../lib/normalizers';
import { getBookBadge } from '../utils/badge';

export default function Home() {
  const [books, setBooks] = useState([]);
  const [trending, setTrending] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [mostRead, setMostRead] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      api.get('/books?limit=50'),
      api.get('/books/trending?limit=10'),
      api.get('/books/bestsellers?limit=10'),
      api.get('/books/most-read?limit=10'),
      api.get('/books/upcoming?limit=10'),
    ])
      .then(([booksData, trendingData, bestsellersData, mostReadData, upcomingData]) => {
        if (cancelled) return;
        const normalized = normalizeBooks(booksData.books);
        if (normalized.length > 0) setBooks(normalized);
        setTrending(normalizeBooks(trendingData.books));
        setBestsellers(normalizeBooks(bestsellersData.books));
        setMostRead(normalizeBooks(mostReadData.books));
        setUpcoming(normalizeBooks(upcomingData.books));
      })
      .catch(() => { })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const { featured, newReleases, freeBooks } = useMemo(() => {
    const byBadge = (label) => books.filter((b) => getBookBadge(b) === label);
    // A book can carry the "New Release" badge (recently published) while
    // still sitting inside its early-access window for this viewer — not
    // yet purchasable by everyone. Those belong in the "Coming Soon"
    // section, not here, so New Releases only ever shows books anyone can
    // buy right now.
    const isPurchasableNow = (b) => !b.earlyAccess || b.earlyAccess.canPurchase;

    const freeBooks = byBadge('Free').slice(0, 8);

    const featured = books
      .filter((b) => b.featured)
      .sort((a, b) => (b.featuredPriority - a.featuredPriority) || (new Date(b.published) - new Date(a.published)))
      .slice(0, 5);
    const newReleases = byBadge('New Release')
      .filter(isPurchasableNow)
      .sort((a, b) => new Date(b.published) - new Date(a.published))
      .slice(0, 8);

    return { featured, newReleases, freeBooks };
  }, [books]);

  return (
    <>
      <Hero />
      <ContinueReading />

      <FeaturedBanner books={featured} loading={loading} />

      <BookSection
        title="Trending Now"
        subtitle="What everyone's reading this week."
        to="/shop?q=trending"
        books={trending}
        loading={loading}
        badgeOverride="Trending"
        eyebrow="Trending"
      />

      <BookSection
        title="New Releases"
        subtitle="Fresh off the (digital) press."
        to="/shop?q=new"
        books={newReleases}
        loading={loading}
        badgeOverride="New Release"
        eyebrow="New Release"
      />

      <BookSection
        title="Coming Soon"
        subtitle="Get early access before everyone else."
        to="/shop?q=upcoming"
        books={upcoming}
        loading={loading}
        badgeOverride="Coming Soon"
        eyebrow="Coming Soon"
      />

      <GenreHighlights />

      <BookSection
        title="Bestsellers"
        subtitle="The titles readers can't stop recommending."
        to="/shop?q=bestselling"
        books={bestsellers}
        loading={loading}
        badgeOverride="Bestseller"
        eyebrow="Bestseller"
      />

      <BookSection
        title="Most Read This Month"
        to="/shop?q=most-read"
        books={mostRead}
        loading={loading}
      />

      <BookSection
        title="Free Books"
        subtitle="No catch — start reading today, on us."
        to="/shop?q=free"
        books={freeBooks}
        loading={loading}
        badgeOverride="Free"
        eyebrow="Free"
      />

      <AffiliateProgramSection />

      <TopAuthors />
      <Testimonials />
    </>
  );
}
